/**
 * Cache management utilities for the configurator
 * Provides methods to clear various caches when needed
 * Now includes cache version detection and targeted cache clearing
 */

/**
 * Clear all client-side caches
 * @returns {Object} Result of cache clearing operations
 */
export const clearAllCaches = () => {
  const results = {
    globalProductCache: false,
    globalPackageCache: false,
    globalFilterCache: false,
    sessionStorage: false,
    localStorage: false,
  };

  try {
    // Clear global caches
    if (window.globalProductCache) {
      window.globalProductCache = {};
      results.globalProductCache = true;
    }

    if (window.globalPackageCache) {
      window.globalPackageCache = {};
      results.globalPackageCache = true;
    }

    if (window.globalFilterCache) {
      window.globalFilterCache = {};
      results.globalFilterCache = true;
    }

    // Clear cache timestamps
    if (window.globalProductCacheTime) {
      window.globalProductCacheTime = {};
    }

    // Clear session storage
    const sessionKeys = [
      's7_package_type',
      's7_package_quantity',
      's7_is_mailbox_session',
      's7_cache_cleared',
    ];

    sessionKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });
    results.sessionStorage = true;

    // Clear relevant localStorage items
    const localKeys = ['s7_draft_data', 's7_auto_save'];

    localKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
    results.localStorage = true;

    return results;
  } catch (error) {
    console.error('Error clearing caches:', error);
    return { error: error.message, ...results };
  }
};

/**
 * Clear server-side caches via API
 * @returns {Promise<Object>} Result of server cache clearing
 */
export const clearServerCaches = async () => {
  try {
    const response = await fetch('/api/configurator/clear-cache', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error clearing server caches:', error);
    return { error: error.message };
  }
};

/**
 * Clear all caches (client + server)
 * @returns {Promise<Object>} Combined result of all cache clearing operations
 */
export const clearAllCachesComplete = async () => {
  const clientResult = clearAllCaches();
  const serverResult = await clearServerCaches();

  return {
    client: clientResult,
    server: serverResult,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Force refresh of specific data
 * @param {string} type - Type of data to refresh ('products', 'packages', 'filters')
 * @returns {Promise<Object>} Result of refresh operation
 */
export const forceRefresh = async (type) => {
  try {
    // Clear relevant caches first
    if (type === 'products' || type === 'all') {
      if (window.globalProductCache) {
        window.globalProductCache = {};
      }
      if (window.globalProductCacheTime) {
        window.globalProductCacheTime = {};
      }
    }

    if (type === 'packages' || type === 'all') {
      if (window.globalPackageCache) {
        window.globalPackageCache = {};
      }
    }

    if (type === 'filters' || type === 'all') {
      if (window.globalFilterCache) {
        window.globalFilterCache = {};
      }
    }

    // Set cache cleared flag for next API calls
    sessionStorage.setItem('s7_cache_cleared', 'true');

    // Dispatch reload event
    document.dispatchEvent(
      new CustomEvent('s7_force_refresh', {
        detail: { type, timestamp: Date.now() },
      }),
    );

    return { success: true, type, timestamp: new Date().toISOString() };
  } catch (error) {
    console.error('Error during force refresh:', error);
    return { error: error.message };
  }
};

/**
 * Get cache status information
 * @returns {Object} Current cache status
 */
export const getCacheStatus = () => {
  return {
    globalProductCache: {
      exists: !!window.globalProductCache,
      size: window.globalProductCache
        ? Object.keys(window.globalProductCache).length
        : 0,
    },
    globalPackageCache: {
      exists: !!window.globalPackageCache,
      size: window.globalPackageCache
        ? Object.keys(window.globalPackageCache).length
        : 0,
    },
    globalFilterCache: {
      exists: !!window.globalFilterCache,
      size: window.globalFilterCache
        ? Object.keys(window.globalFilterCache).length
        : 0,
    },
    sessionStorage: {
      s7_package_type: sessionStorage.getItem('s7_package_type'),
      s7_package_quantity: sessionStorage.getItem('s7_package_quantity'),
      s7_is_mailbox_session: sessionStorage.getItem('s7_is_mailbox_session'),
    },
    timestamp: new Date().toISOString(),
  };
};

/**
 * Clear specific cache sections based on version change
 * @param {string} version - New version detected
 * @returns {Object} Result of targeted cache clearing
 */
export const clearCacheForVersion = (version) => {
  const results = {
    globalProductCache: false,
    globalPackageCache: false,
    globalFilterCache: false,
    versionCleared: version,
  };

  try {
    // Clear only the caches that are affected by version changes
    if (window.globalProductCache) {
      window.globalProductCache = {};
      results.globalProductCache = true;
    }

    if (window.globalPackageCache) {
      window.globalPackageCache = {};
      results.globalPackageCache = true;
    }

    if (window.globalFilterCache) {
      window.globalFilterCache = {};
      results.globalFilterCache = true;
    }

    // Clear cache timestamps
    if (window.globalProductCacheTime) {
      window.globalProductCacheTime = {};
    }

    // Set version cleared flag
    sessionStorage.setItem('s7_version_cleared', version);

    return results;
  } catch (error) {
    console.error('Error clearing cache for version:', error);
    return { error: error.message, ...results };
  }
};

/**
 * Check if cache version has changed
 * @param {string} currentVersion - Current version from API response
 * @returns {boolean} True if version has changed
 */
export const hasVersionChanged = (currentVersion) => {
  const lastVersion = sessionStorage.getItem('s7_last_cache_version');

  if (!lastVersion) {
    // First time, store current version
    sessionStorage.setItem('s7_last_cache_version', currentVersion);
    return false;
  }

  if (lastVersion !== currentVersion) {
    // Version changed, update stored version
    sessionStorage.setItem('s7_last_cache_version', currentVersion);
    return true;
  }

  return false;
};

/**
 * Handle version change detection and targeted cache clearing
 * @param {string} version - Version from API response header
 * @returns {Object} Result of version change handling
 */
export const handleVersionChange = (version) => {
  if (!version) {
    return { changed: false, reason: 'No version header' };
  }

  const changed = hasVersionChanged(version);

  if (changed) {
    const clearResult = clearCacheForVersion(version);

    // Dispatch version change event
    document.dispatchEvent(
      new CustomEvent('s7_cache_version_changed', {
        detail: {
          version,
          clearResult,
          timestamp: Date.now(),
        },
      }),
    );

    return {
      changed: true,
      version,
      clearResult,
    };
  }

  return { changed: false, reason: 'Version unchanged' };
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  window.clearAllCaches = clearAllCaches;
  window.clearServerCaches = clearServerCaches;
  window.clearAllCachesComplete = clearAllCachesComplete;
  window.forceRefresh = forceRefresh;
  window.getCacheStatus = getCacheStatus;
  window.clearCacheForVersion = clearCacheForVersion;
  window.hasVersionChanged = hasVersionChanged;
  window.handleVersionChange = handleVersionChange;
}
